import React, {forwardRef, useRef, useLayoutEffect, useMemo} from 'react';
import {listen, onImageLoad} from 'polyphony/dom';
import {isIE} from 'polyphony/browser';
import {isAdmin, setRef, combineRefs, truncateURL} from 'polyphony/util';
import {useInitialVisibility} from 'polyphony/hooks';

function inlineSvg(svg) {
    if (!svg.startsWith('<?xml')) svg = `<?xml version="1.0" encoding="UTF-8"?>` + svg;
    return isIE() ? 'data:image/svg+xml;base64,' + window.btoa(svg)
                  : 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
}

function isDataURI(url) {
    return url && url.startsWith("data:");
}

function placeholderImage(w, h) {
    return inlineSvg(`<?xml version="1.0" encoding="UTF-8"?><svg xmlns='http://www.w3.org/2000/svg' x="0px" y="0px" width="${w}" height="${h}"></svg>`);
}

// TODO: integrate pixel density ratio
function runAutosize(imgRef = {}) {
    let r = imgRef.current;

    if (r) {
        const {width, height} = r.getBoundingClientRect();
        const wholeWidth = innerWidth; // Actually an accessor on window that causes browser side effects.
        const ratio = Math.max(width / wholeWidth, height / wholeWidth);
        if (ratio > 0) {
            r.sizes = `${100 * ratio}vw`;
        }
    }
}

const buildSrcSetString = srcSet => Object.entries(srcSet).reduce((acc, [k, v], i) => (i === 0 ? '' : acc + ', ') + `${v} ${k}w`, '');

const Image = forwardRef(({
                // img tag props
                alt: _alt = '', // img alt tag

                /* eslint-disable */
                title, // ignore this for accessibility
                /* eslint-enable */

                // Semantic Tags
                onLoad, // called when the image is loaded.
                lazy = true, // is the image lazy?
                decorative = null, // is the image decorative, i.e. is it part of the page (vs the content)

                // Pick one (increasing order or precedence)
                src, // lowest precedence
                srcset,
                    preferredSize, // a size to use out of media.sizes, replaces src.
                sizes, // same prop
                media, // highest precedence
                intrinsicWidth: iw, intrinsicHeight: ih, // how big the image actually is
                ...props}, ref) => {
    //
    /* Calculate the image's props */

    const imgProps = useMemo(() => {
        if (media) {
            const {url: fullUrl, width: fullWidth, sizes: msizes, svg} = media;

            if (svg) return {src: inlineSvg(svg)};

            if (msizes) {
                if (preferredSize && msizes[preferredSize]) {
                    const {url, crop} = msizes[preferredSize];
                    if (!crop) return {src: truncateURL(url)};
                }

                if (!isIE()) {
                    let msrcset = {};
                    if (fullWidth && fullUrl) msrcset[fullWidth] = truncateURL(fullUrl);

                    for (const {width, url, crop} of Object.values(msizes)) {
                        if (!crop && !isDataURI(url)) {
                            msrcset[width] = truncateURL(url);
                        }
                    }

                    return {
                        srcSet: Object.keys(msrcset).length > 1 ? buildSrcSetString(msrcset) : undefined,
                        sizes: sizes ? sizes : undefined
                    };
                }
            }
            return {src: truncateURL(fullUrl)};
        }

        if (srcset && Object.keys(srcset).length > 0) {
            return {
                srcSet: typeof srcset !== 'string' ? buildSrcSetString(srcset) : srcset ? srcset : undefined,
                sizes: sizes ? sizes : undefined
            };
        }

        if (src) {
            return {src: truncateURL(src)};
        }
        return {};
    }, [src, srcset, media]);

    //
    /* Answer questions about the image */

    // Now make sure we're not making a data URI or empty image lazy
    const isEmpty = !imgProps.src && !imgProps.srcSet;
    const isData = imgProps.src && isDataURI(imgProps.src);
    const isAutosizable = !!imgProps.sizes && imgProps.srcSet;

    // We don't want to unlazy twice. Since we already downloaded the image,
    // let's assume it's already in the cache and skip the rigamarole.
    const hasUnlaziedRef = useRef(false);

    lazy = lazy && !hasUnlaziedRef.current && !isData && !isEmpty && !isAdmin();

    /*
        Calc props for <img /> tag
    */

    const alt = decorative ? '' : _alt ? _alt : media ? media.alt : '';

    // If we're lazy && we have width information
    // Make a placeholder image so the image takes up enough space to
    // be picked up by an intersection observer.
    let attrs = {...props, alt, role: 'img', draggable:"false"};
    if (lazy) {
        attrs.src = (iw || (media && media.width)) && (ih || (media && media.height)) ? placeholderImage(iw ? iw : media.width, ih ? ih : media.height) : null; // eslint-disable-line
        attrs.style = {...props.style || {}, visibility: 'hidden'};
    } else {
        attrs = {...attrs, ...imgProps};
    }
    if (decorative) {
        attrs.focusable = 'false';
        attrs['aria-hidden'] = 'true';
        attrs.tabIndex = '-1';
    }

    //
    /* Do our shit! */

    const imgRef = useInitialVisibility(lazyImage => {
        if (lazy) {
            const {src, srcSet, sizes} = imgProps;

            lazyImage.removeAttribute('src');

            if (src || srcSet) {
                if (srcSet) {
                    if (sizes) {
                        lazyImage.sizes = sizes;
                    }
                    lazyImage.srcset = srcSet;
                } else if (src) {
                    lazyImage.src = src;
                }

                onImageLoad(lazyImage, () => {
                    let vis = (props.style || {}).visibility || '';
                    lazyImage.style.visibility = vis;

                    setRef(hasUnlaziedRef, true);

                    if (onLoad) onLoad();
                });
            }
        }
    }, [imgProps, ref, onLoad, lazy]);

    useLayoutEffect(() => {
       if (isAutosizable) {
           runAutosize(imgRef);
           return listen(window, 'resize', () => runAutosize(imgRef), true);
       }
       return () => {};
    }, [imgRef.current, isAutosizable]);

    const onLoadRef = onLoad && !lazy ? r => r ? onImageLoad(r, onLoad) : null : undefined;

    return <img {...attrs}
                ref={combineRefs([imgRef, ref, onLoadRef])} />;
});

Image.displayName = 'Image';

export default Image;

